import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
    AdminEntity,
    BalanceProvider,
    BokkzAlgDataService,
    BokkzJrDataService,
    BokkzJrLogic,
    ClientEntity,
    CorporateTaxEntity,
    IncomeTaxEntity,
    JaarrapportageEntity,
    JaarrekeningEntity,
    KolommenbalansService,
    PmClient,
} from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { catchError, combineLatest, Observable, of, Subscriber, Subscription, throwError } from 'rxjs';
import { ToastService } from '@snelstart/ui-toast';
import { Router } from '@angular/router';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-koppelen-rgs-admin',
    templateUrl: './koppelen-rgs-admin.component.html',
    imports: [
        SharedUiDetailsModule,
        SharedUiButtonModule,
        SharedUiAlertModule,
        ReactiveFormsModule,
        SharedUiFormControlsModule,
        CommonModule,
    ],
    styleUrls: ['./koppelen-rgs-admin.component.css'],
})
export class KoppelenRgsAdminComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    public jrEntity = {} as JaarrapportageEntity;

    private routerSubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private corporateTaxEntitySubscription: Subscription | undefined;

    public clientEntity = {} as ClientEntity;
    public sstAdminIdFg = {} as FormGroup;
    public adminEntityList = {} as AdminEntity[];
    public administrationList: SelectFormControlItem<string>[] = [];
    public showSpinner = false;
    public isDefAangifte = {} as boolean;
    public externalProviderId = {} as string;
    public externalEnvironmentId = {} as string;
    public hasExternalProvider = false;
    public loginStatus = false;

    administratieInlezen = ButtonConfiguration.create({
        onClick: () =>
            new Observable<void>((subscription) => {
                this.getImportAdmin(subscription);
            }),
    });

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    constructor(
        public algDataService: BokkzAlgDataService,
        public jrDataService: BokkzJrDataService,
        private jrLogicService: BokkzJrLogic,
        public pmClient: PmClient,
        private kolommenbalansService: KolommenbalansService,
        public toastService: ToastService,
        private router: Router,
        private fb: FormBuilder,
    ) {
        this.sstAdminIdFg = this.fb.group({
            id: null as unknown as string,
            name: null as unknown as string,
            rgs: null as unknown as string,
            inlezen: null as unknown as boolean,
            administrationId: null as unknown as string,
            providerId: null as unknown as string,
        });
    }

    onSelect(administrationId: string) {
        const selectedAdmin = this.clientEntity.adminEntities?.find((option) => option.administrationId === administrationId);
        if (selectedAdmin && selectedAdmin.administrationId) {
            if (selectedAdmin.providerId) {
                this.hasExternalProvider = true;
                this.externalProviderId = selectedAdmin.providerId;
                this.externalEnvironmentId = selectedAdmin.environmentId;
                this.checkExterneKoppeling(selectedAdmin);
            } else {
                this.hasExternalProvider = false;
                this.externalProviderId = null as unknown as string;
            }
            this.jrEntity.sstAdminId = selectedAdmin.administrationId;
            this.sstAdminIdFg.patchValue(selectedAdmin);
            this.saveForm();
        }
    }

    public ngOnInit(): void {
        this.algDataService.client.subscribe((value) => {
            this.clientEntity = value;
            if (value != null) {
                this.administrationList = [];
                if (value.adminEntities !== undefined) {
                    this.adminEntityList = value.adminEntities;
                    for (let i = 0; this.adminEntityList[i]; i++) {
                        const admininistration = {
                            value: this.adminEntityList[i].administrationId!.toString(),
                            label: this.adminEntityList[i].name!.toString(),
                        };
                        this.administrationList.push(admininistration);
                    }
                }
            }
        });
        this.jrDataService.jaarrapportageEntity.subscribe((value) => {
            this.jrEntity = value;
        });
        const jaarrapportageEntityObservable = this.jrDataService.jaarrapportageEntity.pipe(
            catchError((error) => {
                this.toastService.showErrorMessage('Error loading corporate tax entity data');
                console.log(error);
                return of(null);
            }),
        );
        const clientEntityObservable = this.algDataService.client.pipe(
            catchError((error) => {
                this.toastService.showErrorMessage('Error loading client entity data');
                console.log(error);
                return of(null);
            }),
        );
        combineLatest([jaarrapportageEntityObservable, clientEntityObservable]).subscribe(([jaarrapportageEntity, clientEntity]) => {
            if (jaarrapportageEntity && clientEntity) {
                this.jrEntity = jaarrapportageEntity;
                this.clientEntity = clientEntity;
                if (clientEntity) {
                    this.administrationList = [];
                    const value = clientEntity;
                    if (value.adminEntities !== undefined) {
                        this.adminEntityList = value.adminEntities;
                        for (let i = 0; this.adminEntityList[i]; i++) {
                            const admininistration = {
                                value: this.adminEntityList[i].administrationId!.toString(),
                                label: this.adminEntityList[i].name!.toString(),
                            };
                            this.administrationList.push(admininistration);
                        }
                    }
                }
                if (this.jrEntity.rapportage) {
                    if (this.jrEntity.sstAdminId !== undefined) {
                        const id = this.jrEntity.sstAdminId;
                        const gekoppeldeAdmin = this.adminEntityList.find((item) => item.administrationId?.toString() === id);
                        if (gekoppeldeAdmin !== undefined) {
                            if (gekoppeldeAdmin.providerId) {
                                this.hasExternalProvider = this.checkExterneKoppeling(gekoppeldeAdmin);
                            }
                            this.sstAdminIdFg.patchValue(
                                {
                                    inlezen: true,
                                    name: gekoppeldeAdmin.name,
                                    administrationId: gekoppeldeAdmin.administrationId,
                                    providerId: gekoppeldeAdmin.providerId,
                                    rgs: gekoppeldeAdmin.rgs,
                                },
                                { emitEvent: false },
                            );
                        }
                    }
                }
            } else {
                this.toastService.showErrorMessage('Some data could not be loaded');
            }
        });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.corporateTaxEntitySubscription) {
            this.corporateTaxEntitySubscription.unsubscribe(); // Unsubscribe from income tax entity
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe(); // Unsubscribe from income tax entity
        }
    }
    getImportAdmin(subscription: Subscriber<void>) {
        if (this.jrEntity.sstAdminId) {
            if (this.hasExternalProvider) {
                if (this.externalProviderId == 'exactonline') {
                    this.executeBalanceBaseProvider(subscription, this.jrEntity, false, this.jrEntity.sstAdminId);
                }
                if (this.externalProviderId == 'minox') {
                    this.executeBalanceBaseProviderEnvironment(subscription, this.jrEntity, false, this.jrEntity.sstAdminId);
                }
            } else {
                this.kolommenbalansService
                    .getUpdatedAangifteMetAdministratie(
                        this.jrEntity.sstAdminId,
                        String(this.clientEntity.sstRelationId),
                        this.jrEntity,
                        false,
                        this.clientEntity.sstPrimaryAdminId!,
                    )
                    .then((entity) => {
                        if (entity) {
                            this.jrDataService.updateEntity(entity as JaarrapportageEntity);
                        }
                        setTimeout(() => {
                            //TODO: rekenservice toevoegen.
                            // this.vpbRekenService.getCorporateTaxCalculation(entity.aangifte).subscribe((vpbBerekening) => {
                            //     this.dataVpbService.updateVpbBerekening(vpbBerekening);
                            //   });
                            this.toastService.showSuccessMessage('De administratie is succesvol ingelezen in de jaarrapportage.');
                            subscription.next();
                            subscription.complete();
                        }, 250);
                    })
                    .catch((error) => {
                        subscription.next();
                        subscription.complete();
                        if (error.error) {
                            if (JSON.stringify(error.error).includes('RGS-code is leeg')) {
                                this.toastService.showErrorMessage(
                                    'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. De foutmelding is als volgt: ' +
                                        error.error +
                                        '. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                                );
                            } else {
                                this.toastService.showErrorMessage(
                                    'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. De administratie is namelijk niet (volledig) RGS-ready. Pas de RGS-codes in de administratie eerst aan of ga verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                                );
                            }
                        } else {
                            this.toastService.showErrorMessage(
                                'Helaas is er een technisch probleem waardoor de koppeling niet succesvol is verlopen. Wij werken hard aan een oplossing. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                            );
                        }
                    });
            }
        } else {
            subscription.next();
            subscription.complete();
            this.toastService.showErrorMessage(
                'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
            );
        }
        this.showSpinner = true;
        setTimeout(() => {
            this.showSpinner = false;
        }, 1000);
    }

    public checkData() {
        if (this.sstAdminIdFg.get('inlezen')?.value === false) {
            this.jrEntity.sstAdminId = undefined;
            this.sstAdminIdFg.patchValue({
                inlezen: false,
                adminId: undefined,
                adminName: undefined,
            });
            if (this.jrEntity.sstAdminId) {
                this.jrEntity.sstAdminId = undefined;
            }
            this.saveForm();
        }
    }

    saveForm(): void {
        const updatedEntity = {
            ...this.jrEntity,
            sstAdminId: this.sstAdminIdFg.getRawValue().administrationId,
        } as JaarrapportageEntity;
        this.jrDataService.updateEntity(updatedEntity);
    }

    private executeBalanceBaseProvider(
        subscription: Subscriber<void>,
        entity: IncomeTaxEntity | CorporateTaxEntity | JaarrekeningEntity,
        partnerBoolean: boolean,
        adminId: string,
    ) {
        const kolommmenBalans = this.pmClient.postBalanceProviderBalances(
            String(this.clientEntity.sstRelationId),
            String(this.externalProviderId),
            adminId,
            partnerBoolean,
            entity,
        );
        kolommmenBalans.subscribe({
            next: (entity) => {
                this.jrEntity = entity as JaarrekeningEntity;
                this.jrLogicService.getCalculation(entity);
                subscription.next();
                subscription.complete();
            },
            error: () => {
                subscription.next();
                subscription.complete();
                this.toastService.showErrorMessage(
                    'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                );
            },
            complete: () => {
                this.saveForm();
                this.toastService.showSuccessMessage(
                    `De administratie '${this.sstAdminIdFg.get('name')?.getRawValue()}' is succesvol ingelezen.`,
                );
            },
        });
    }

    private executeBalanceBaseProviderEnvironment(
        subscription: Subscriber<void>,
        entity: IncomeTaxEntity | CorporateTaxEntity | JaarrekeningEntity,
        partnerBoolean: boolean,
        adminId: string,
    ) {
        const kolommmenBalans = this.pmClient.postBalanceProviderBalancesEnvironment(
            String(this.clientEntity.sstRelationId),
            String(this.externalProviderId),
            String(this.externalEnvironmentId),
            adminId,
            partnerBoolean,
            entity,
        );
        kolommmenBalans.subscribe({
            next: (entity) => {
                this.jrEntity = entity as JaarrekeningEntity;
                this.jrLogicService.getCalculation(entity);
                subscription.next();
                subscription.complete();
            },
            error: () => {
                subscription.next();
                subscription.complete();
                this.toastService.showErrorMessage(
                    'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                );
            },
            complete: () => {
                this.saveForm();
                this.toastService.showSuccessMessage(
                    `De administratie '${this.sstAdminIdFg.get('name')?.getRawValue()}' is succesvol ingelezen.`,
                );
            },
        });
    }

    private checkExterneKoppeling(gekoppeldeAdmin: AdminEntity) {
        if (gekoppeldeAdmin.providerId) {
            this.externalProviderId = gekoppeldeAdmin.providerId;
            this.pmClient.getIdentityProviderId(String(gekoppeldeAdmin.providerId)).subscribe({
                next: (result) => {
                    this.loginStatus = result['authenticated'];
                },
                error: (error) => {
                    this.loginStatus = false;
                    this.toastService.showWarningMessage(error);
                },
                complete: () => {
                    console.log('complete ');
                },
            });
            return true;
        }
        return false;
    }

    public goToExternalProvider() {
        const selectedProviderId = this.externalProviderId;
        this.pmClient
            .getBalanceProvider(selectedProviderId)
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            )
            .subscribe({
                next: (results: BalanceProvider) => {
                    this.pmClient.getIdentityProviders(results.id).subscribe({
                        next: (singleResult) => {
                            console.log(singleResult.uri);
                            window.open(singleResult.uri, '_blank');
                        },
                    });
                },
                error: (error) => {
                    console.log(error);
                    this.toastService.showWarningMessage(error);
                },
                complete: () => {
                    console.log('complete');
                },
            });
    }
}
